/**
 * Admin -- Settings/General (Wave 1b W1b-01 wired)
 *
 * Sections:
 *   1. Tenant Identity -- name, slug (read-only), UUID, business unit, cost center, contacts
 *   2. Tenant Settings -- allow web search, allow external connections
 *   3. Deployment Mode -- IQ-Connected/Standalone/Air-Gapped (read-only display)
 *   4. Regional & Locale -- timezone, date format, business hours
 *   4. Environment Metadata -- env label, maintenance mode, debug mode, version
 *   5. Danger Zone -- slug rename, factory reset
 *
 * Mock data has been removed. First paint pulls from
 * /api/v1/admin/settings/general; mutations PATCH the same surface or
 * call the dedicated POST endpoints (/contacts, /maintenance-mode,
 * /read-only-mode, /debug-mode, /rename-slug). Every mutation emits an
 * audit_chain row server-side.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import {
  Building2, Server, Globe, Activity, Copy, AlertTriangle,
  User as UserIcon, Mail, Phone, Loader2, Save,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  SettingField, SectionCard, DangerZone, DangerAction, StatusDot,
} from '@/components/admin';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/admin/settings/')({
  component: GeneralPage,
});

interface GeneralResponse {
  identity: {
    id: string;
    name: string;
    slug: string;
    orgCode: string;
    isRoot: boolean;
    createdAt: string;
    tenantName: string | null;
    businessUnit: string | null;
    costCenter: string | null;
  };
  contacts: {
    primary: { name: string; email: string; phone: string | null } | null;
    technical: { name: string; email: string; phone: string | null } | null;
    compliance: { name: string; email: string; phone: string | null } | null;
  };
  deploymentMode: {
    mode: 'iq_connected' | 'standalone' | 'air_gapped';
    health: { status: 'healthy' | 'degraded' | 'unknown'; detail: string | null };
  };
  locale: Record<string, unknown>;
  /**
   * The two tenant settings (Foundations 1 / B0-T1). `null` when the server
   * could not read them — the page says so rather than rendering two `off`
   * switches that look like a deliberate choice.
   */
  policy: {
    allowWebSearch: boolean;
    allowExternalConnections: boolean;
  } | null;
  ops: {
    maintenanceMode: boolean;
    readOnlyMode: boolean;
    debugMode: boolean;
    debugModeUntil: string | null;
  };
  environment: {
    environment: string;
    version: string;
    buildId: string;
  };
  changedMeta: Record<
    string,
    { changedBy: string | null; changedAt: string; changeReason: string | null }
  >;
}

function GeneralPage() {
  const [data, setData] = useState<GeneralResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Editable identity fields (initialised from server payload).
  const [tenantName, setTenantName] = useState('');
  const [businessUnit, setBusinessUnit] = useState('');
  const [costCenter, setCostCenter] = useState('');

  // Locale fields
  const [defaultLocale, setDefaultLocale] = useState('en-US');
  const [defaultTimezone, setDefaultTimezone] = useState('America/New_York');
  const [dateFormat, setDateFormat] = useState('MMM d, yyyy');
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('24h');
  const [currency, setCurrency] = useState('USD');
  const [fiscalYearStart, setFiscalYearStart] = useState('01-01');
  const [businessHoursStart, setBusinessHoursStart] = useState('09:00');
  const [businessHoursEnd, setBusinessHoursEnd] = useState('17:00');

  // Saving / dialog state
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [savingLocale, setSavingLocale] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [factoryResetOpen, setFactoryResetOpen] = useState(false);
  const [maintenanceModePendingEnable, setMaintenanceModePendingEnable] =
    useState(false);
  const [readOnlyModePendingEnable, setReadOnlyModePendingEnable] =
    useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiClient.get<GeneralResponse>('/admin/settings/general/');
      setData(r);
      setTenantName(r.identity.tenantName ?? r.identity.name);
      setBusinessUnit(r.identity.businessUnit ?? '');
      setCostCenter(r.identity.costCenter ?? '');
      const loc = r.locale ?? {};
      setDefaultLocale((loc.default_locale as string | null) ?? 'en-US');
      setDefaultTimezone(
        (loc.default_timezone as string | null) ?? 'America/New_York'
      );
      setDateFormat((loc.date_format as string | null) ?? 'MMM d, yyyy');
      setTimeFormat((loc.time_format as '12h' | '24h' | null) ?? '24h');
      setCurrency((loc.currency as string | null) ?? 'USD');
      setFiscalYearStart((loc.fiscal_year_start as string | null) ?? '01-01');
      setBusinessHoursStart(
        (loc.business_hours_start as string | null) ?? '09:00'
      );
      setBusinessHoursEnd(
        (loc.business_hours_end as string | null) ?? '17:00'
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load settings';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function saveIdentity() {
    try {
      setSavingIdentity(true);
      await apiClient.patch('/admin/settings/general/', {
        tenantName: tenantName.trim() || undefined,
        businessUnit: businessUnit.trim() || null,
        costCenter: costCenter.trim() || null,
        reason: 'Identity update from admin UI',
      });
      toast.success('Tenant identity updated');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSavingIdentity(false);
    }
  }

  // The two tenant settings. Each flip is its own audited PATCH — there is no
  // "save" button for a switch, and the server is the source of truth for the
  // new value (we refresh rather than optimistically flipping local state, so
  // a refused write cannot leave the UI claiming a capability the gate denies).
  async function savePolicy(
    key: 'allowWebSearch' | 'allowExternalConnections',
    value: boolean
  ) {
    try {
      setSavingPolicy(true);
      await apiClient.patch('/admin/settings/general/', {
        [key]: value,
        reason: 'Tenant settings update from admin UI',
      });
      toast.success(
        key === 'allowWebSearch'
          ? `Web search ${value ? 'allowed' : 'turned off'}`
          : `External connections ${value ? 'allowed' : 'turned off'}`
      );
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
      // Re-read so the switch snaps back to whatever the server actually holds.
      await refresh();
    } finally {
      setSavingPolicy(false);
    }
  }

  /**
   * Establish the two settings when the page could not read them.
   *
   * Review BF-06: `resolveTenantSettings` fails closed on a MISSING row, and
   * the page renders the outage banner INSTEAD of the switches — so the write
   * that would create the rows was unreachable from the UI and only a direct
   * DB insert fixed it. This sends both keys in one audited PATCH (the same
   * endpoint the switches use, `setTenantSetting` upserts), writing the safe
   * posture: both off. The admin then turns on what they want, deliberately.
   */
  async function establishPolicy() {
    try {
      setSavingPolicy(true);
      await apiClient.patch('/admin/settings/general/', {
        allowWebSearch: false,
        allowExternalConnections: false,
        reason: 'Establish tenant policy settings from admin UI',
      });
      toast.success('Tenant settings established — both are off until you allow them');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not establish the settings';
      toast.error(msg);
      await refresh();
    } finally {
      setSavingPolicy(false);
    }
  }

  async function saveLocale() {
    try {
      setSavingLocale(true);
      await apiClient.patch('/admin/settings/general/', {
        locale: {
          default_locale: defaultLocale,
          default_timezone: defaultTimezone,
          date_format: dateFormat,
          time_format: timeFormat,
          currency,
          fiscal_year_start: fiscalYearStart,
          business_hours_start: businessHoursStart,
          business_hours_end: businessHoursEnd,
        },
        reason: 'Locale update from admin UI',
      });
      toast.success('Locale settings updated');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSavingLocale(false);
    }
  }

  async function setMode(
    field: 'maintenance-mode' | 'read-only-mode' | 'debug-mode',
    enabled: boolean,
    reason: string,
    extra?: Record<string, unknown>
  ) {
    try {
      await apiClient.post(`/admin/settings/general/${field}`, {
        enabled,
        reason,
        ...(extra ?? {}),
      });
      toast.success(
        `${field === 'debug-mode' ? 'Debug' : field === 'maintenance-mode' ? 'Maintenance' : 'Read-only'} mode ${enabled ? 'enabled' : 'disabled'}`
      );
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Toggle failed';
      toast.error(msg);
    }
  }

  if (loading || !data) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading settings...
        </div>
      </div>
    );
  }

  const identityMeta = data.changedMeta['tenant_name'];
  const buMeta = data.changedMeta['business_unit'];
  const ccMeta = data.changedMeta['cost_center'];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-semibold">General</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Tenant identity, deployment mode, locale, and environment metadata.
        </p>
      </div>

      {/* 1. Tenant Identity */}
      <SectionCard
        title="Tenant Identity"
        description="Information about this tenant. Slug and UUID are permanent."
        icon={Building2}
        actions={
          <Button
            size="sm"
            className="h-8 gap-1.5"
            onClick={saveIdentity}
            disabled={savingIdentity}
            title="Save identity changes"
          >
            {savingIdentity ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save changes
          </Button>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          {/*
           * Bug #992 — Playwright probes locate identity inputs by
           * data-testid (`tenant-name-input`, etc). Without the testid
           * the cycle-3.5 deep-pass probe couldn't see the field even
           * though the page rendered correctly. Mirrors the testid
           * pattern already used by other admin forms.
           */}
          <SettingField
            label="Tenant name"
            description="Display name shown throughout the product."
            changedBy={identityMeta?.changedBy ?? undefined}
            changedAt={identityMeta?.changedAt}
          >
            <Input
              data-testid="tenant-name-input"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              className="h-9 text-xs"
              // Issue #992 / ADMIN-SET-GEN-001 — give the input a stable
              // test selector so deep-pass probes don't bounce on the
              // generic `input` locator. Same pattern as the other
              // labelled inputs on this page.
              data-testid="tenant-name-input"
              name="tenantName"
              aria-label="Tenant name"
            />
          </SettingField>

          <SettingField
            label="Tenant slug"
            description="URL-safe identifier. Cannot be changed without 2-person approval."
            readOnly
            readOnlyReason="Requires 2-person approval to rename"
          >
            <div className="flex items-center gap-2">
              <Input
                data-testid="tenant-slug-input"
                value={data.identity.slug}
                disabled
                className="h-9 text-xs font-mono"
              />
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 h-9"
                onClick={() => setRenameOpen(true)}
                title="Request a slug rename (2-person approval required)"
              >
                Request rename
              </Button>
            </div>
          </SettingField>

          <SettingField
            label="Tenant UUID"
            description="Internal unique identifier. Referenced in API calls and audit logs."
            readOnly
            readOnlyReason="Permanent identifier"
          >
            <div className="flex items-center gap-2">
              <Input
                data-testid="tenant-uuid-input"
                value={data.identity.id}
                disabled
                className="h-9 text-xs font-mono"
              />
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 shrink-0"
                // Issue #997 — stable aria-label + data-testid for the
                // icon-only copy button (title alone leaves SR/Playwright
                // without selectors).
                title="Copy tenant UUID"
                aria-label="Copy tenant UUID"
                data-testid="copy-tenant-uuid"
                onClick={() => {
                  navigator.clipboard.writeText(data.identity.id);
                  toast.success('UUID copied');
                }}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </SettingField>

          <SettingField
            label="Business unit"
            description="For chargeback and reporting."
            changedBy={buMeta?.changedBy ?? undefined}
            changedAt={buMeta?.changedAt}
          >
            <Input
              data-testid="tenant-business-unit-input"
              value={businessUnit}
              onChange={(e) => setBusinessUnit(e.target.value)}
              className="h-9 text-xs"
            />
          </SettingField>

          <SettingField
            label="Cost center"
            changedBy={ccMeta?.changedBy ?? undefined}
            changedAt={ccMeta?.changedAt}
          >
            <Input
              data-testid="tenant-cost-center-input"
              value={costCenter}
              onChange={(e) => setCostCenter(e.target.value)}
              className="h-9 text-xs"
            />
          </SettingField>

        </div>

        <div className="border-t pt-4 mt-2">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">
            Contacts
          </p>
          <div className="space-y-3">
            <ContactRow
              label="Primary contact"
              icon={UserIcon}
              contact={data.contacts.primary}
            />
            <ContactRow
              label="Technical contact"
              icon={UserIcon}
              contact={data.contacts.technical}
            />
            <ContactRow
              label="Compliance officer"
              icon={UserIcon}
              contact={data.contacts.compliance}
            />
          </div>
        </div>
      </SectionCard>

      {/* 2. Tenant Settings — the only two per-tenant policy switches.
          Foundations 1 / B0-T1 (decisions.md 2026-09-08, "Features, flags and
          plans"): the Features tab and its 22 toggles are gone; every rail is
          simply on. These two decide whether this tenant may reach outside,
          and they are the tenant admin's, not the sysadmin's. */}
      <SectionCard
        title="Tenant Settings"
        description="Whether this tenant may reach outside. Every rail and every capability is on."
        icon={Globe}
      >
        {data.policy === null ? (
          <div
            className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2"
            data-testid="tenant-policy-unreadable"
          >
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div className="space-y-2">
              <div>
                These settings could not be read. Until that is fixed both
                capabilities are refused — this is an outage, not a choice you made.
              </div>
              {/* The recovery path (review BF-06). The commonest cause is a
                  tenant created by a path that never established the two rows;
                  the write endpoint UPSERTS, so establishing them is a normal
                  audited PATCH — it was simply unreachable while the banner
                  replaced the switches. Writes the safe posture (both off) and
                  re-reads; if the store itself is down the PATCH fails and the
                  banner stays, which is the honest answer. */}
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                data-testid="establish-tenant-policy"
                disabled={savingPolicy}
                onClick={establishPolicy}
              >
                Establish these settings
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <SettingField
              label="Allow web search"
              description="Let people turn on live web search per turn in the chat composer. Results are marked as an unverified Web source."
              changedBy={
                data.changedMeta['policy.allow_web_search']?.changedBy ?? undefined
              }
              changedAt={data.changedMeta['policy.allow_web_search']?.changedAt}
            >
              <Switch
                data-testid="setting-allow-web-search"
                checked={data.policy.allowWebSearch}
                disabled={savingPolicy}
                onCheckedChange={(v) => savePolicy('allowWebSearch', v)}
              />
            </SettingField>

            <SettingField
              label="Allow external connections"
              description="Let this tenant connect out to outside systems — connectors and MCP servers. Off means fully inward-facing."
              changedBy={
                data.changedMeta['policy.allow_external_connections']?.changedBy ??
                undefined
              }
              changedAt={
                data.changedMeta['policy.allow_external_connections']?.changedAt
              }
            >
              <Switch
                data-testid="setting-allow-external-connections"
                checked={data.policy.allowExternalConnections}
                disabled={savingPolicy}
                onCheckedChange={(v) => savePolicy('allowExternalConnections', v)}
              />
            </SettingField>
          </div>
        )}
      </SectionCard>

      {/* 3. Deployment Mode */}
      <SectionCard
        title="Deployment Mode"
        description="How this tenant is deployed. Changing modes requires sysadmin coordination."
        icon={Server}
      >
        <div className="flex items-center gap-3 rounded-md border bg-muted/20 p-3">
          <div
            className={cn(
              'h-10 w-10 rounded-md flex items-center justify-center shrink-0',
              data.deploymentMode.mode === 'air_gapped' && 'bg-emerald-500/10',
              data.deploymentMode.mode === 'iq_connected' && 'bg-blue-500/10',
              data.deploymentMode.mode === 'standalone' && 'bg-violet-500/10'
            )}
          >
            <Server
              className={cn(
                'h-5 w-5',
                data.deploymentMode.mode === 'air_gapped' &&
                  'text-emerald-600 dark:text-emerald-400',
                data.deploymentMode.mode === 'iq_connected' &&
                  'text-blue-600 dark:text-blue-400',
                data.deploymentMode.mode === 'standalone' &&
                  'text-violet-600 dark:text-violet-400'
              )}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold">
                {data.deploymentMode.mode === 'iq_connected' && 'IQ-Connected'}
                {data.deploymentMode.mode === 'standalone' && 'Standalone'}
                {data.deploymentMode.mode === 'air_gapped' && 'Air-Gapped'}
              </span>
              <StatusDot
                status={
                  data.deploymentMode.health.status === 'healthy'
                    ? 'healthy'
                    : 'degraded'
                }
              />
              <span className="text-[11px] text-muted-foreground capitalize">
                {data.deploymentMode.health.status}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {data.deploymentMode.mode === 'iq_connected' &&
                'Validates ArgusIQ-issued JWTs. Data flows from IQ via MCP.'}
              {data.deploymentMode.mode === 'standalone' &&
                'Issues own JWTs. Full user management + SSO.'}
              {data.deploymentMode.mode === 'air_gapped' &&
                'No external network calls. Fully self-contained on premise.'}
            </p>
          </div>
        </div>
      </SectionCard>

      {/* 4. Regional & Locale */}
      <SectionCard
        title="Regional & Locale"
        description="Default timezone, date format, and business hours."
        icon={Globe}
        actions={
          <Button
            size="sm"
            className="h-8 gap-1.5"
            onClick={saveLocale}
            disabled={savingLocale}
            title="Save locale changes"
          >
            {savingLocale ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save changes
          </Button>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <SettingField label="Default locale">
            <Select value={defaultLocale} onValueChange={setDefaultLocale}>
              <SelectTrigger aria-label="Default locale" className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en-US" className="text-xs">English (United States)</SelectItem>
                <SelectItem value="en-GB" className="text-xs">English (United Kingdom)</SelectItem>
                <SelectItem value="es-ES" className="text-xs">Spanish (Spain)</SelectItem>
                <SelectItem value="de-DE" className="text-xs">German (Germany)</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField label="Default timezone">
            <Select value={defaultTimezone} onValueChange={setDefaultTimezone}>
              <SelectTrigger aria-label="Default timezone" className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York"    className="text-xs">America/New_York (ET)</SelectItem>
                <SelectItem value="America/Chicago"     className="text-xs">America/Chicago (CT)</SelectItem>
                <SelectItem value="America/Denver"      className="text-xs">America/Denver (MT)</SelectItem>
                <SelectItem value="America/Los_Angeles" className="text-xs">America/Los_Angeles (PT)</SelectItem>
                <SelectItem value="Europe/London"       className="text-xs">Europe/London</SelectItem>
                <SelectItem value="Europe/Berlin"       className="text-xs">Europe/Berlin</SelectItem>
                <SelectItem value="Asia/Tokyo"          className="text-xs">Asia/Tokyo</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField label="Date format">
            <Select value={dateFormat} onValueChange={setDateFormat}>
              <SelectTrigger aria-label="Date format" className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MMM d, yyyy"   className="text-xs">Apr 20, 2026</SelectItem>
                <SelectItem value="yyyy-MM-dd"    className="text-xs">2026-04-20</SelectItem>
                <SelectItem value="d MMM yyyy"    className="text-xs">20 Apr 2026</SelectItem>
                <SelectItem value="MM/dd/yyyy"    className="text-xs">04/20/2026</SelectItem>
                <SelectItem value="dd/MM/yyyy"    className="text-xs">20/04/2026</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField label="Time format">
            <Select value={timeFormat} onValueChange={(v) => setTimeFormat(v as '12h' | '24h')}>
              <SelectTrigger aria-label="Time format" className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24h" className="text-xs">24-hour (17:30)</SelectItem>
                <SelectItem value="12h" className="text-xs">12-hour (5:30 PM)</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField label="Currency">
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger aria-label="Currency" className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD" className="text-xs">USD - US Dollar</SelectItem>
                <SelectItem value="EUR" className="text-xs">EUR - Euro</SelectItem>
                <SelectItem value="GBP" className="text-xs">GBP - British Pound</SelectItem>
                <SelectItem value="JPY" className="text-xs">JPY - Japanese Yen</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField label="Fiscal year start">
            <Input
              value={fiscalYearStart}
              onChange={(e) => setFiscalYearStart(e.target.value)}
              placeholder="MM-DD"
              className="h-9 text-xs font-mono"
            />
          </SettingField>
        </div>

        <div className="border-t pt-4 mt-2">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">
            Business hours
          </p>
          <div className="grid grid-cols-2 gap-4">
            <SettingField label="Start">
              <Input
                value={businessHoursStart}
                onChange={(e) => setBusinessHoursStart(e.target.value)}
                type="time"
                className="h-9 text-xs font-mono"
              />
            </SettingField>
            <SettingField label="End">
              <Input
                value={businessHoursEnd}
                onChange={(e) => setBusinessHoursEnd(e.target.value)}
                type="time"
                className="h-9 text-xs font-mono"
              />
            </SettingField>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            Used for notification quiet-hour defaults and scheduled agent runs.
          </p>
        </div>
      </SectionCard>

      {/* 4. Environment Metadata */}
      <SectionCard
        title="Environment"
        description="Operational state and build metadata."
        icon={Activity}
      >
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Fact
            label="Environment"
            value={
              <Badge
                variant="outline"
                className="text-[10px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
              >
                {data.environment.environment}
              </Badge>
            }
          />
          <Fact
            label="Version"
            value={<code className="text-[11px]">{data.environment.version}</code>}
          />
          <Fact
            label="Build"
            value={<code className="text-[11px]">{data.environment.buildId}</code>}
          />
          <Fact
            label="Tenant created"
            value={
              <span className="tabular-nums text-[11px]">
                {new Date(data.identity.createdAt).toLocaleDateString()}
              </span>
            }
          />
        </div>

        <div className="border-t pt-4 space-y-3">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            Operational modes
          </p>

          <ModeToggle
            label="Maintenance mode"
            description="Blocks all non-admin traffic. Admin banner shown. Use during migrations."
            enabled={data.ops.maintenanceMode}
            onChange={(v) => {
              if (v) {
                setMaintenanceModePendingEnable(true);
              } else {
                setMode(
                  'maintenance-mode',
                  false,
                  'Disabled from admin UI'
                );
              }
            }}
            destructive
          />

          <ModeToggle
            label="Read-only mode"
            description="Disables all write operations. Useful during backup restore or DR testing."
            enabled={data.ops.readOnlyMode}
            onChange={(v) => {
              if (v) {
                setReadOnlyModePendingEnable(true);
              } else {
                setMode('read-only-mode', false, 'Disabled from admin UI');
              }
            }}
            destructive
          />

          <ModeToggle
            label="Debug mode"
            description={
              data.ops.debugModeUntil
                ? `Auto-disables at ${new Date(data.ops.debugModeUntil).toLocaleTimeString()}`
                : 'Enables verbose logging. Auto-disables after 1-24 hours.'
            }
            enabled={data.ops.debugMode}
            onChange={(v) => {
              if (v) {
                setMode('debug-mode', true, 'Enabled from admin UI', {
                  ttlHours: 4,
                });
              } else {
                setMode('debug-mode', false, 'Disabled from admin UI');
              }
            }}
          />
        </div>
      </SectionCard>

      {/* Danger Zone */}
      <DangerZone>
        <DangerAction
          title="Export all tenant data"
          description="Generate a full tenant export for offboarding or DR. Use the Data & Backup tab for details."
          action={
            <Button
              size="sm"
              variant="outline"
              onClick={() => toast.info('Navigate to Data & Backup tab')}
              title="Open Data & Backup tab"
            >
              Open Data & Backup
            </Button>
          }
        />
        <DangerAction
          title="Factory reset this tenant"
          description="Wipes all configuration, users, and data. Requires 2-person approval + typed confirmation. Cannot be undone."
          action={
            <Button
              size="sm"
              variant="outline"
              className="border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10"
              onClick={() => setFactoryResetOpen(true)}
              title="Factory reset (irreversible, 2-person approval required)"
            >
              Factory reset...
            </Button>
          }
        />
      </DangerZone>

      {/* Dialogs */}
      <SlugRenameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        currentSlug={data.identity.slug}
        onRenamed={async () => {
          setRenameOpen(false);
          await refresh();
        }}
      />

      <TypedConfirmDialog
        open={factoryResetOpen}
        onOpenChange={setFactoryResetOpen}
        title="Factory reset tenant"
        description="This permanently wipes ALL data, users, agents, documents, and configuration for this tenant. This action cannot be undone."
        consequences={[
          'All users deactivated and records deleted',
          'All conversations and agent executions deleted',
          'All uploaded documents deleted (hard delete)',
          'All custom agents, tools, and integrations removed',
          'API keys revoked; webhooks disabled',
          'Backups are preserved for 30 days (restore possible within window)',
          'Audit log is preserved (tamper-evident)',
        ]}
        confirmPhrase={`reset ${data.identity.slug}`}
        confirmLabel="Permanently factory reset"
        requireReason
        reasonPlaceholder="Required: explain why you're wiping this tenant"
        require2Person
        variant="danger"
        onConfirm={() =>
          toast.info(
            'Factory reset endpoint is deferred to Wave 1b Stage 5 (Data & Backup).'
          )
        }
      />

      <TypedConfirmDialog
        open={maintenanceModePendingEnable}
        onOpenChange={setMaintenanceModePendingEnable}
        title="Enter maintenance mode"
        description="Maintenance mode blocks all non-admin traffic. Users see a maintenance banner. Only use when you actively need to block tenant access."
        consequences={[
          'All non-admin traffic returns 503 Service Unavailable',
          'Maintenance banner is shown to admin users',
          'Background jobs continue running',
          'Can be disabled at any time to restore access',
        ]}
        confirmPhrase="enter maintenance"
        confirmLabel="Enter maintenance"
        requireReason
        reasonPlaceholder="e.g. DB migration - ticket OPS-4821"
        variant="warning"
        onConfirm={async () => {
          setMaintenanceModePendingEnable(false);
          await setMode(
            'maintenance-mode',
            true,
            'Enabled from admin UI - typed-confirm acknowledged'
          );
        }}
      />

      <TypedConfirmDialog
        open={readOnlyModePendingEnable}
        onOpenChange={setReadOnlyModePendingEnable}
        title="Enter read-only mode"
        description="Read-only mode disables all write operations. Reads continue to work. Use during backup restore or DR testing."
        consequences={[
          'All writes return 405 Method Not Allowed',
          'Reads continue normally',
          'Queued writes are held - they do not fail',
          'Can be disabled at any time to resume writes',
        ]}
        confirmPhrase="enter read-only"
        confirmLabel="Enter read-only"
        requireReason
        reasonPlaceholder="e.g. Restoring from backup - ticket DR-0042"
        variant="warning"
        onConfirm={async () => {
          setReadOnlyModePendingEnable(false);
          await setMode(
            'read-only-mode',
            true,
            'Enabled from admin UI - typed-confirm acknowledged'
          );
        }}
      />
    </div>
  );
}

// ─── Slug rename dialog ─────────────────────────────────────────────────────

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

function SlugRenameDialog({
  open, onOpenChange, currentSlug, onRenamed,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currentSlug: string;
  onRenamed: () => Promise<void>;
}) {
  const [newSlug, setNewSlug] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [reason, setReason] = useState('');
  const [approverEmail, setApproverEmail] = useState('');
  const [saving, setSaving] = useState(false);

  function reset() {
    setNewSlug('');
    setConfirmPhrase('');
    setReason('');
    setApproverEmail('');
  }

  async function submit() {
    if (newSlug.length < 3) {
      toast.error('New slug must be at least 3 characters');
      return;
    }
    if (confirmPhrase !== currentSlug) {
      toast.error(`Confirm phrase must equal "${currentSlug}"`);
      return;
    }
    if (reason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    try {
      setSaving(true);
      await apiClient.post('/admin/settings/general/rename-slug', {
        newSlug,
        confirmPhrase,
        reason,
        approverEmail: approverEmail.trim() || undefined,
      });
      toast.success(`Slug renamed to "${newSlug}"`);
      reset();
      await onRenamed();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Rename failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename tenant slug</DialogTitle>
          <DialogDescription>
            Renaming a slug requires 2-person approval and is gated by an
            active legal-hold check. The audit chain records both
            approver identities.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="rename-current">Current slug</Label>
            <Input
              id="rename-current"
              value={currentSlug}
              disabled
              className="h-9 text-xs font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rename-new">New slug</Label>
            <Input
              id="rename-new"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder="e.g. acme-prod"
              className="h-9 text-xs font-mono"
            />
            <p className="text-[10px] text-muted-foreground">
              Lowercase letters, digits, hyphens. 3-50 characters.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rename-confirm">
              Type <code className="font-mono">{currentSlug}</code> to confirm
            </Label>
            <Input
              id="rename-confirm"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              className="h-9 text-xs font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rename-approver">Second approver email</Label>
            <Input
              id="rename-approver"
              value={approverEmail}
              onChange={(e) => setApproverEmail(e.target.value)}
              placeholder="another-admin@example.com"
              className="h-9 text-xs"
            />
            <p className="text-[10px] text-muted-foreground">
              Required by the 2-person rule for tenant-write operations.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rename-reason">Reason</Label>
            <Input
              id="rename-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Rebranding"
              className="h-9 text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Submit rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">
        {label}
      </div>
      <div className="text-xs">{value}</div>
    </div>
  );
}

function ContactRow({
  label,
  icon: Icon,
  contact,
}: {
  label: string;
  icon: any;
  contact: { name: string; email: string; phone: string | null } | null;
}) {
  if (!contact) {
    return (
      <div className="flex items-center gap-3 rounded-md border p-2.5 bg-muted/10">
        <div className="h-8 w-8 rounded-md bg-muted/40 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            {label}
          </div>
          <div className="text-xs italic text-muted-foreground">
            Not configured
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-md border p-2.5">
      <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
          {label}
        </div>
        <div className="text-xs font-medium truncate">{contact.name}</div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Mail className="h-2.5 w-2.5" /> {contact.email}
          </span>
          {contact.phone && (
            <span className="inline-flex items-center gap-1">
              <Phone className="h-2.5 w-2.5" /> {contact.phone}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ModeToggle({
  label, description, enabled, onChange, destructive = false,
}: {
  label: string;
  description: string;
  enabled: boolean;
  onChange: (v: boolean) => void;
  destructive?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <div className="min-w-0 flex-1">
        <div className={cn(
          'text-xs font-semibold flex items-center gap-1.5',
          enabled && destructive && 'text-amber-700 dark:text-amber-400',
        )}>
          {destructive && enabled && <AlertTriangle className="h-3 w-3" />}
          {label}
          {enabled && (
            <Badge variant="outline" className={cn(
              'text-[9px]',
              destructive
                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30'
                : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
            )}>
              Active
            </Badge>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">{description}</p>
      </div>
      <Switch checked={enabled} onCheckedChange={onChange} className="shrink-0 mt-1" />
    </div>
  );
}
